/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

import { Inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { UploadAssetService } from '../../common/upload-asset.service';
import {
  languageMap, SUPPORTED_FORMATS, UPPY_DASHBOARD_OPTIONS, UPPY_IMAGE_EDITOR_OPTION,
} from './uppy-config';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';

export enum UploadType {
  AUDIO,
  VIDEO,
  IMAGE,
  FILE,
}
export interface UppyUploadOption {
  dropTarget? : string; // Css element selector for drag and drop file.
  callback? : (res: any) => void; // Will be called after upload process completed.
  type : UploadType; // Type of media
  buttonClickTarget?: string; // Attach css element selector. Dashboard will be open on click.
}
/**
 * Angular service that open Uppy Dashboard to perform upload, crop and rotate.
 */
@Injectable()
export class UppyUploadService {
  /**
   * Uppy instance
   */
  private Uppy;

  /**
   * Uppy dashboard
   */
  private Dashboard;

  /**
   * Uppy drag and drop target
   */
  private DropTarget;

  /**
   * Uppy image editor
   */
  private ImageEditor;

  /**
   * Constructor.
   *
   * @param translocoService
   * @param uploadAssetService Upload asset service.
   * @param bulbAuthService
   * @param BulbBusyCoverService BulbBusyCoverService for busy loader.
   * @param ASSET_LIBRARY Constant file to get content type.
   */
  constructor(
    private translocoService: TranslocoService,
    private uploadAssetService: UploadAssetService,
    @Inject('BulbAuthService') private bulbAuthService,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
    @Inject('ASSET_LIBRARY') private ASSET_LIBRARY,
  ) {}

  /**
   * Initialize the uppy instance and configure it using UppyUploadOption
   *
   * @param option @see UppyUploadOption
   */
  initUppyDashboard(option: UppyUploadOption): Promise<any> {
    // Lazy load uppy core, dashboard, imageEditor and drag and Drop modules.
    return Promise.all([
      import('@uppy/core'),
      import('@uppy/dashboard'),
      import('@uppy/image-editor'),
      import('@uppy/drop-target'),
      import(`@uppy/locales/lib/${languageMap[this.bulbAuthService.currentUser.language.toLowerCase()]}`),
    ]).then(([Uppy, Dashboard, ImageEditor, DropTarget, UserLanguage]) => {
      this.Uppy = Uppy.default;
      this.Dashboard = Dashboard.default;
      this.ImageEditor = ImageEditor.default;
      this.DropTarget = DropTarget.default;

      const autoProceed = option.type !== UploadType.IMAGE;
      this.uploadAssetService.uppy = new this.Uppy(
        {
          ...this.getUppyOption(this.getAllowedTypes(option.type), autoProceed),
          allowMultipleUploadBatches: false,
          locale: UserLanguage.default,
        },
      )
        .use(this.Dashboard, { ...UPPY_DASHBOARD_OPTIONS, ...this.addMissingTranslationKeys(UserLanguage.default) });

      this.uploadAssetService.initializeUppyAwsS3();

      // If dropTarget is available, then initialize and configure drag and drop
      if (option.dropTarget) {
        this.initDrop(option.dropTarget);
      }

      // Configure image editor for cropping and rotating image.
      this.uploadAssetService.uppy.use(this.ImageEditor, {
        ...UPPY_IMAGE_EDITOR_OPTION,
        target: this.Dashboard,
      });

      // Uppy callback handler when file editing is complete.
      this.uploadAssetService.uppy.on('file-editor:complete', () => {
        switch (option.type) {
          case UploadType.IMAGE:
            this.handleImageUpload(option);
            break;
          default:
            break;
        }
      });
      this.uploadAssetService.handleError();
    });
  }

  /**
   * Method to add missing translations
   * @param userLanguage
   */
  addMissingTranslationKeys(userLanguage) {
    const missingKeys = { save: 'saveButton' };
    const strings: any = {};

    for (const key of Object.keys(missingKeys)) {
      // if the key does not exist in uppy translation json, then we need to add it.
      if (!userLanguage.strings[key]) {
        strings[key] = this.translocoService.translate(missingKeys[key]);
      }
    }
    return {
      locale: {
        strings,
      },
    };
  }

  /**
   * Remove all registered uppy events.
   */
  closeAndUnregisterEvents() {
    if (this.uploadAssetService.uppy) {
      this.uploadAssetService.uppy.close();
    }
  }

  /**
   * Open the uppy dashboard for selecting and uploading file
   */
  openUppyDashboard() {
    this.uploadAssetService.uppy.getPlugin('Dashboard').openModal();
  }

  /**
   * Initialize drop area on requested element using uppy.
   *
   * @param target css element selector
   */
  private initDrop(target): void {
    this.uploadAssetService.uppy.use(this.DropTarget, {
      target,
      onDrop: (event) => {
        this.openUppyDashboard();
      },
    });
  }

  /**
   * Handle image upload.
   *
   * @param option For upload process. @see UppyUploadOption.
   * @private
   */
  private handleImageUpload(option) {
    const hideBusy: () => void = this.BulbBusyCoverService.showBusy();
    this.uploadAssetService.uppy.upload().then((result) => {
      this.closeAndUnregisterEvents();
      hideBusy();
      option.callback(result?.successful[0].response.body.url);
    });
  }

  /**
   * Get Uppy option.
   *
   * @param allowedType Type of content supported to upload.
   * @param autoProceed True if upload file after choose.
   * @private
   */
  private getUppyOption(allowedType: string, autoProceed? : boolean) {
    const finalUppyConfigurationOptions: any = this.uploadAssetService.generateUppyUploadOptions(allowedType);
    finalUppyConfigurationOptions.autoProceed = autoProceed || false;
    return finalUppyConfigurationOptions;
  }

  /**
   * Get type of supported media for upload.
   *
   * @param type Upload type.
   * @private
   */
  private getAllowedTypes(type: UploadType) : any {
    switch (type) {
      case UploadType.IMAGE:
        return SUPPORTED_FORMATS.IMAGE;
      case UploadType.AUDIO:
        return this.ASSET_LIBRARY.FILTER.AUDIO;
      case UploadType.VIDEO:
        return this.ASSET_LIBRARY.FILTER.VIDEO;
      default:
        break;
    }
  }
}
